import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tag, Loader2, Ban, Plus } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Tab „Coduri promo": creează cupoane Stripe — câmpul „Adaugă cod promoțional"
// apare automat pe pagina de plată găzduită de Stripe (allow_promotion_codes).
export function PromoCodesPanel() {
  const [codes, setCodes] = useState(null);
  const [eroare, setEroare] = useState("");
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ code: "", percentOff: "", amountOffRon: "", maxRedemptions: "", expiresInDays: "" });

  const load = () => {
    axios
      .get(`${API}/admin/promo-codes`, { withCredentials: true })
      .then((r) => setCodes(r.data.codes))
      .catch(() => setEroare("Nu am putut încărca codurile promo."));
  };

  useEffect(load, []);

  const creeaza = async () => {
    if (!form.code.trim()) {
      toast.error("Scrie un cod (ex: VARA2026)");
      return;
    }
    if (!form.percentOff && !form.amountOffRon) {
      toast.error("Alege un procent SAU o sumă fixă de reducere");
      return;
    }
    setBusy(true);
    try {
      await axios.post(
        `${API}/admin/promo-codes`,
        {
          code: form.code,
          percentOff: form.percentOff ? Number(form.percentOff) : null,
          amountOffRon: form.amountOffRon ? Number(form.amountOffRon) : null,
          maxRedemptions: form.maxRedemptions ? Number(form.maxRedemptions) : null,
          expiresInDays: form.expiresInDays ? Number(form.expiresInDays) : null,
        },
        { withCredentials: true }
      );
      toast.success(`Cod „${form.code.toUpperCase()}" creat`);
      setForm({ code: "", percentOff: "", amountOffRon: "", maxRedemptions: "", expiresInDays: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Crearea codului a eșuat");
    } finally {
      setBusy(false);
    }
  };

  const dezactiveaza = async (id) => {
    setBusy(true);
    try {
      await axios.post(`${API}/admin/promo-codes/${id}/deactivate`, {}, { withCredentials: true });
      toast.success("Cod dezactivat");
      load();
    } catch {
      toast.error("Dezactivarea a eșuat");
    } finally {
      setBusy(false);
    }
  };

  if (eroare) return <p className="text-center text-rose-500 py-10">{eroare}</p>;

  return (
    <div className="space-y-4" data-testid="promo-codes-panel">
      <div className="rounded-2xl bg-white border border-slate-100 shadow-sm p-4 space-y-2.5">
        <p className="text-sm font-semibold text-slate-700">Cod nou</p>
        <Input
          data-testid="promo-code-input"
          value={form.code}
          onChange={(e) => setForm({ ...form, code: e.target.value })}
          placeholder="Cod (ex: VARA2026)"
          className="rounded-xl"
        />
        <div className="grid grid-cols-2 gap-2">
          <Input
            data-testid="promo-percent-input"
            type="number"
            value={form.percentOff}
            onChange={(e) => setForm({ ...form, percentOff: e.target.value, amountOffRon: "" })}
            placeholder="% reducere"
            className="rounded-xl"
          />
          <Input
            data-testid="promo-amount-input"
            type="number"
            value={form.amountOffRon}
            onChange={(e) => setForm({ ...form, amountOffRon: e.target.value, percentOff: "" })}
            placeholder="RON reducere fixă"
            className="rounded-xl"
          />
          <Input
            data-testid="promo-max-redemptions-input"
            type="number"
            value={form.maxRedemptions}
            onChange={(e) => setForm({ ...form, maxRedemptions: e.target.value })}
            placeholder="Max. utilizări (opțional)"
            className="rounded-xl"
          />
          <Input
            data-testid="promo-expires-input"
            type="number"
            value={form.expiresInDays}
            onChange={(e) => setForm({ ...form, expiresInDays: e.target.value })}
            placeholder="Expiră în (zile, opțional)"
            className="rounded-xl"
          />
        </div>
        <Button
          data-testid="create-promo-btn"
          onClick={creeaza}
          disabled={busy}
          className="w-full rounded-xl bg-emerald-600 hover:bg-emerald-700 font-bold"
        >
          <Plus className="h-4 w-4 mr-1.5" /> Creează codul
        </Button>
      </div>

      {!codes ? (
        <Loader2 className="h-6 w-6 animate-spin text-emerald-600 mx-auto mt-6" />
      ) : codes.length === 0 ? (
        <p className="text-center text-slate-400 text-sm py-6">Niciun cod promo creat încă.</p>
      ) : (
        <div className="space-y-2">
          {codes.map((c) => (
            <div
              key={c.id}
              data-testid={`promo-row-${c.code}`}
              className="rounded-2xl bg-white border border-slate-100 shadow-sm p-3.5 flex items-center justify-between gap-3"
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <Tag className={`h-4 w-4 shrink-0 ${c.active ? "text-emerald-500" : "text-slate-300"}`} />
                <div className="min-w-0">
                  <p className="font-mono font-bold text-sm text-slate-900">{c.code}</p>
                  <p className="text-[11px] text-slate-400">
                    {c.percentOff ? `${c.percentOff}% reducere` : `${c.amountOffRon} RON reducere`} ·{" "}
                    {c.timesRedeemed}/{c.maxRedemptions || "∞"} folosiri
                  </p>
                </div>
              </div>
              {c.active ? (
                <button
                  data-testid={`deactivate-promo-${c.code}`}
                  onClick={() => dezactiveaza(c.id)}
                  disabled={busy}
                  className="shrink-0 h-8 w-8 flex items-center justify-center rounded-lg border border-rose-200 text-rose-500 hover:bg-rose-50 transition-colors"
                  title="Dezactivează"
                >
                  <Ban className="h-3.5 w-3.5" />
                </button>
              ) : (
                <span className="text-[11px] text-slate-400 shrink-0">inactiv</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
